import { API_BASE_URL } from "@/lib/env";
import {
  buildSubscribeFeedPayload,
  type PodcastHistoryMode,
} from "@/lib/subscription-history";

export interface APIPodcast {
  podcast_slug: string;
  display_name: string;
  emoji: string;
  description: string | null;
  episode_count: number;
  latest_episode_date: string | null;
  taxonomy: {
    Format?: string[];
    Theme?: string[];
    Topic?: string[];
  } | null;
}

export interface APIEpisode {
  id: string;
  title: string;
  description: string | null;
  episode_number: number;
  audio_url: string;
  duration: number | null;
  published_date: string | null;
  podcast_slug: string;
  podcast_title: string | null;
  podcast_display_name: string | null;
  is_indexed: boolean;
  tags: string | { Theme?: string[]; Topic?: string[]; Format?: string[] } | null;
}

export interface AskRequest {
  question: string;
  previous_response_id?: string;
  k?: number;
  max_context_tokens?: number;
  mmr_diversity?: number;
  score_threshold?: number;
}

export interface AskResponse {
  answer: string;
  verdict: "answer" | "insufficient";
  used_context: "current" | "previous" | "both";
  missing: string[];
  response_id: string;
  model: string;
  chunks_used: number;
  referenced_episode_numbers?: number[];
  metadata?: AskResponseMetadata | null;
}

export interface AskReferencedEpisodeMetadata {
  episode_id?: number | null;
  episode_number: number;
  title?: string | null;
  title_key?: string | null;
  published_date?: string | null;
  chunks_used: number;
  evidence_roles: Record<string, number>;
}

export interface AskSourceMetadata {
  source_id: string;
  episode_id?: number | null;
  episode_number?: number | null;
  title?: string | null;
  title_key?: string | null;
  published_date?: string | null;
  chunk_index?: number | null;
  speaker_name?: string | null;
  speaker_label?: string | null;
  start_char?: number | null;
  end_char?: number | null;
  start_time_ms?: number | null;
  end_time_ms?: number | null;
  evidence_role: string;
  content_origin: string;
  retrieval_source?: string | null;
  selection_reason?: string | null;
  score?: number | null;
  text_preview?: string | null;
}

export interface AskAnswerDiagnosticsMetadata {
  reason: string;
  relation_guard_triggered: boolean;
  relation_claims_suppressed: boolean;
  source_attribution_obligation: boolean;
  candidate_support_count: number;
  selected_support_count: number;
  context_items: number;
  context_evidence_roles: Record<string, number>;
}

export interface AskQueryMetadata {
  kind: string;
  retrieval_budget: string;
  is_relation_question: boolean;
  temporal?: AskTemporalMetadata | null;
  filters: {
    episode_number?: number | null;
    title_key?: string | null;
    max_context_tokens: number;
    requested_k?: number | null;
    score_threshold?: number | null;
  };
}

export interface AskTemporalMetadata {
  mode: string;
  confidence: string;
  hard_filter_applied: boolean;
  reason: string;
  source: string;
  llm_action?: string | null;
  llm_model?: string | null;
  llm_risk_flags?: string[] | null;
  start_date?: string | null;
  end_date?: string | null;
  resolved_episode_count: number;
  resolved_episode_numbers_preview: number[];
}

export interface AskPerformanceMetadata {
  latency_ms: number;
  chunks_used: number;
  context_tokens_est: number;
}

export interface AskResponseMetadata {
  referenced_episodes: AskReferencedEpisodeMetadata[];
  sources: AskSourceMetadata[];
  answer_diagnostics: AskAnswerDiagnosticsMetadata;
  query: AskQueryMetadata;
  performance: AskPerformanceMetadata;
}

export interface APISubscription {
  podcast_slug: string;
  notify_enabled: boolean;
  history_mode?: PodcastHistoryMode | null;
  import_start_date?: string | null;
  feed_url?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  display_name?: string | null;
  emoji?: string | null;
  podcast?: {
    display_name?: string | null;
    emoji?: string | null;
    description?: string | null;
  } | null;
}

export interface EpisodeCountResponse {
  podcast_slug: string;
  display_name: string;
  episode_count: number;
  cached_at?: string | null;
  refreshed?: boolean;
}

export interface SubscribeToFeedResponse {
  ok: boolean;
  subscribed?: {
    slug: string;
    accepted_url: string;
    primary_key: string | null;
    matched_by: string | null;
    history_mode: PodcastHistoryMode;
    import_start_date: string | null;
  };
  status?: "backfill_pending" | "following_new_episodes" | string;
  error?: string | null;
}

export interface SubscribeToFeedOptions {
  notifyEnabled?: boolean;
  idempotencyKey?: string;
  historyMode: PodcastHistoryMode;
}

export interface UpdateSubscriptionNotificationResponse {
  ok: boolean;
  subscription?: APISubscription | null;
  error?: string | null;
}

export interface McpTokenCredential {
  created_at: string;
  last_used_at: string | null;
  active_token_count: number;
}

export interface McpTokenStatusResponse {
  server_url: string;
  credential: McpTokenCredential | null;
}

export interface McpTokenSecretResponse extends McpTokenStatusResponse {
  token: string;
}

export type MessagingChannel = "signal" | "telegram" | "whatsapp";
export type MessagingProvider =
  | "signal"
  | "telegram_bot_api"
  | "twilio"
  | "signal_e2e";
export type MessagingLinkState = "unlinked" | "pending" | "linked" | "opted_out" | "unlinking";
export type WhatsAppLinkState = MessagingLinkState;
export type WhatsAppIdentityType =
  | "phone_and_username"
  | "username_only"
  | "provider_user_id";

export interface MessagingPublicIdentityMetadata {
  country_code?: string | null;
  identity_type?: WhatsAppIdentityType | null;
  provider?: MessagingProvider | null;
}

export type WhatsAppPublicIdentityMetadata = MessagingPublicIdentityMetadata;

export interface MessagingEligibility {
  subscription_active?: boolean;
  can_use_messaging?: boolean;
}

export interface MessagingWhatsAppStatus extends WhatsAppPublicIdentityMetadata {
  state: MessagingLinkState;
  linked_at?: string | null;
  consent_at?: string | null;
}

export type MessagingSignalStatus = MessagingWhatsAppStatus;
export type MessagingTelegramStatus = MessagingWhatsAppStatus;

export interface MessagingRetention {
  raw_content_days?: number;
  conversation_days?: number;
  audit_days?: number;
}

export interface MessagingStatusResponse {
  eligibility?: MessagingEligibility | null;
  signal?: MessagingSignalStatus | null;
  signal_entry_url?: string | null;
  telegram?: MessagingTelegramStatus | null;
  telegram_entry_url?: string | null;
  whatsapp?: MessagingWhatsAppStatus | null;
  whatsapp_entry_url?: string | null;
  retention?: MessagingRetention | null;
}

export interface MessagingSetupInspectionResponse extends WhatsAppPublicIdentityMetadata {
  valid: boolean;
  expires_at?: string | null;
  channel?: MessagingChannel | null;
}

export interface MessagingAuthContinuation extends WhatsAppPublicIdentityMetadata {
  token: string;
  expires_at?: string | null;
}

export type LinkMessagingRequest = {
  setup_token: string;
  auth_continuation?: never;
  consent: true;
  consent_version: string;
} | {
  auth_continuation: string;
  setup_token?: never;
  consent: true;
  consent_version: string;
};

export type LinkWhatsAppRequest = LinkMessagingRequest;

export interface UnlinkMessagingResult {
  status: MessagingStatusResponse;
  pending: boolean;
  retryAfterMs: number;
}

export type UnlinkWhatsAppResult = UnlinkMessagingResult;

export function parseRetryAfterMs(value: string | null, fallbackSeconds = 2): number {
  const safeFallbackSeconds = Number.isFinite(fallbackSeconds)
    ? Math.min(Math.max(fallbackSeconds, 0), 10)
    : 2;
  const parsed = value && /^\d+$/.test(value.trim())
    ? Number.parseInt(value, 10)
    : safeFallbackSeconds;
  const safeSeconds = Number.isFinite(parsed) ? Math.min(Math.max(parsed, 0), 10) : safeFallbackSeconds;
  return safeSeconds * 1000;
}

export function mcpTokenEndpoint(baseURL: string, action?: "rotate"): string {
  const base = baseURL.replace(/\/+$/, "");
  return `${base}/me/mcp-token${action ? `/${action}` : ""}`;
}

export function messagingEndpoint(
  baseURL: string,
  endpoint: "status" | "inspect" | MessagingChannel = "status",
): string {
  const base = baseURL.replace(/\/+$/, "");
  if (endpoint === "inspect") return `${base}/messaging/connect/inspect`;
  if (endpoint === "signal") return `${base}/me/messaging/signal`;
  if (endpoint === "telegram") return `${base}/me/messaging/telegram`;
  if (endpoint === "whatsapp") return `${base}/me/messaging/whatsapp`;
  return `${base}/me/messaging`;
}

export function createBillingIdempotencyKey(operation: string): string {
  const safeOperation = operation.replace(/[^a-z0-9_-]/gi, "-").slice(0, 32) || "mutation";
  const nonce = typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : Array.from(crypto.getRandomValues(new Uint8Array(16)), byte => byte.toString(16).padStart(2, "0")).join("");
  return `web:${safeOperation}:${nonce}`;
}

type UnauthorizedHandler = (requestToken: string | null) => void;
type TokenProvider = () => string | null;

class APIClient {
  private readonly baseURL: string;
  private tokenProvider: TokenProvider = () => null;
  private unauthorizedHandler: UnauthorizedHandler | null = null;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
  }

  setTokenProvider(provider: TokenProvider) {
    this.tokenProvider = provider;
  }

  setUnauthorizedHandler(handler: UnauthorizedHandler) {
    this.unauthorizedHandler = handler;
  }

  async authorizedFetch(path: string, init: RequestInit = {}) {
    const headers = new Headers(init.headers ?? {});
    const token = this.tokenProvider();

    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    const response = await fetch(path, {
      ...init,
      headers,
      cache: "no-store",
    });

    if (response.status === 401 && this.unauthorizedHandler) {
      this.unauthorizedHandler(token);
    }

    return response;
  }

  private async parseJSON<T>(response: Response, errorMessage: string): Promise<T> {
    if (!response.ok) {
      const body = await response.text().catch(() => "");
      throw new Error(`${errorMessage}: ${response.status} ${response.statusText}${body ? ` - ${body}` : ""}`);
    }
    return response.json() as Promise<T>;
  }

  async fetchPodcasts(): Promise<APIPodcast[]> {
    const response = await this.authorizedFetch(`${this.baseURL}/me/podcasts`, {
      method: "GET",
      headers: { Accept: "application/json" },
    });

    return this.parseJSON<APIPodcast[]>(response, "Failed to fetch podcasts");
  }

  async fetchEpisodes(podcastSlug?: string): Promise<APIEpisode[]> {
    const url = podcastSlug
      ? `${this.baseURL}/me/episodes?podcast_slug=${encodeURIComponent(podcastSlug)}`
      : `${this.baseURL}/me/episodes`;

    const response = await this.authorizedFetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
    });

    return this.parseJSON<APIEpisode[]>(response, "Failed to fetch episodes");
  }

  async askPodcast(podcastSlug: string, payload: AskRequest): Promise<AskResponse> {
    const response = await this.authorizedFetch(`${this.baseURL}/podcasts/${encodeURIComponent(podcastSlug)}/ask`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    });

    return this.parseJSON<AskResponse>(response, "Failed to ask question");
  }

  async fetchSubscriptions(): Promise<APISubscription[]> {
    const response = await this.authorizedFetch(`${this.baseURL}/me/subscriptions`, {
      method: "GET",
      headers: { Accept: "application/json" },
    });

    return this.parseJSON<APISubscription[]>(response, "Failed to fetch subscriptions");
  }

  async getMcpToken(): Promise<McpTokenStatusResponse> {
    const response = await this.authorizedFetch(mcpTokenEndpoint(this.baseURL), {
      method: "GET",
      headers: { Accept: "application/json" },
    });

    return this.parseJSON<McpTokenStatusResponse>(response, "Failed to fetch MCP token status");
  }

  async createMcpToken(): Promise<McpTokenSecretResponse> {
    const response = await this.authorizedFetch(mcpTokenEndpoint(this.baseURL), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({}),
    });

    return this.parseJSON<McpTokenSecretResponse>(response, "Failed to create MCP token");
  }

  async rotateMcpToken(): Promise<McpTokenSecretResponse> {
    const response = await this.authorizedFetch(mcpTokenEndpoint(this.baseURL, "rotate"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({}),
    });

    return this.parseJSON<McpTokenSecretResponse>(response, "Failed to rotate MCP token");
  }

  async revokeMcpToken(): Promise<void> {
    const response = await this.authorizedFetch(mcpTokenEndpoint(this.baseURL), {
      method: "DELETE",
      headers: { Accept: "application/json" },
    });

    if (!response.ok && response.status !== 204) {
      const body = await response.text().catch(() => "");
      throw new Error(
        `Failed to revoke MCP token: ${response.status} ${response.statusText}${body ? ` - ${body}` : ""}`,
      );
    }
  }

  async getMessagingStatus(): Promise<MessagingStatusResponse> {
    const response = await this.authorizedFetch(messagingEndpoint(this.baseURL), {
      method: "GET",
      headers: { Accept: "application/json" },
    });

    return this.parseJSON<MessagingStatusResponse>(response, "Failed to fetch messaging status");
  }

  async inspectMessagingSetup(setupToken: string): Promise<MessagingSetupInspectionResponse> {
    const response = await fetch(messagingEndpoint(this.baseURL, "inspect"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ token: setupToken }),
      cache: "no-store",
      referrerPolicy: "no-referrer",
    });

    return this.parseJSON<MessagingSetupInspectionResponse>(response, "Failed to inspect messaging setup");
  }

  async linkMessaging(
    channel: MessagingChannel,
    request: LinkMessagingRequest,
  ): Promise<MessagingStatusResponse> {
    const response = await this.authorizedFetch(messagingEndpoint(this.baseURL, channel), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(request),
      referrerPolicy: "no-referrer",
    });

    return this.parseJSON<MessagingStatusResponse>(response, `Failed to link ${channel}`);
  }

  async linkWhatsApp(request: LinkWhatsAppRequest): Promise<MessagingStatusResponse> {
    return this.linkMessaging("whatsapp", request);
  }

  async unlinkMessaging(channel: MessagingChannel): Promise<UnlinkMessagingResult> {
    const response = await this.authorizedFetch(messagingEndpoint(this.baseURL, channel), {
      method: "DELETE",
      headers: { Accept: "application/json" },
    });

    const status = await this.parseJSON<MessagingStatusResponse>(response, `Failed to unlink ${channel}`);
    const channelStatus = channel === "signal"
      ? status.signal
      : channel === "telegram"
        ? status.telegram
        : status.whatsapp;
    const pending = response.status === 202 || channelStatus?.state === "unlinking";
    return {
      status,
      pending,
      retryAfterMs: parseRetryAfterMs(response.headers.get("Retry-After")),
    };
  }

  async unlinkWhatsApp(): Promise<UnlinkWhatsAppResult> {
    return this.unlinkMessaging("whatsapp");
  }

  async updateSubscriptionNotification(
    podcastSlug: string,
    enabled: boolean,
  ): Promise<UpdateSubscriptionNotificationResponse> {
    const response = await this.authorizedFetch(
      `${this.baseURL}/me/subscriptions/${encodeURIComponent(podcastSlug)}?enabled=${enabled}`,
      {
        method: "PATCH",
        headers: { Accept: "application/json" },
      },
    );

    if (response.status === 204) {
      return { ok: true, subscription: null };
    }

    return this.parseJSON<UpdateSubscriptionNotificationResponse>(response, "Failed to update subscription");
  }

  async resolveEpisodeCount(feedUrl: string) {
    const response = await this.authorizedFetch(`${this.baseURL}/me/podcasts/episode-count`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        feed_url: feedUrl.trim(),
      }),
    });

    return this.parseJSON<EpisodeCountResponse>(response, "Failed to resolve podcast feed");
  }

  async subscribeToFeed(
    feedUrl: string,
    options: SubscribeToFeedOptions,
  ) {
    const payload = buildSubscribeFeedPayload(
      feedUrl,
      options.notifyEnabled ?? true,
      options.historyMode,
    );

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    if (options.idempotencyKey) {
      headers["Idempotency-Key"] = options.idempotencyKey;
    }

    const response = await this.authorizedFetch(`${this.baseURL}/me/subscriptions/rss`, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });

    return this.parseJSON<SubscribeToFeedResponse>(response, "Failed to subscribe to podcast");
  }

  async createCheckoutSession(planKey: string, idempotencyKey: string) {
    const response = await this.authorizedFetch(`${this.baseURL}/me/billing/checkout`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "Idempotency-Key": idempotencyKey,
      },
      body: JSON.stringify({
        plan_key: planKey,
        success_url: `${window.location.origin}/billing/success`,
        cancel_url: `${window.location.origin}/paywall`,
      }),
    });

    return this.parseJSON<{ session_id: string; checkout_url: string }>(
      response,
      "Failed to create checkout session",
    );
  }

  async getCreditGrantSummary(): Promise<CreditGrantSummary> {
    const response = await this.authorizedFetch(`${this.baseURL}/me/credit-grant-summary`, {
      method: "GET",
      headers: { Accept: "application/json" },
    });

    return this.parseJSON<CreditGrantSummary>(response, "Failed to fetch credit summary");
  }

  async topupCredits(amountCents: number, idempotencyKey: string): Promise<TopupResponse> {
    const response = await this.authorizedFetch(`${this.baseURL}/billing/topup`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "Idempotency-Key": idempotencyKey,
      },
      body: JSON.stringify({
        credit_type: "chat",
        amount_cents: amountCents,
      }),
    });

    return this.parseJSON<TopupResponse>(response, "Failed to top up credits");
  }

  async updatePaymentMethod(
    successUrl: string,
    cancelUrl: string,
    idempotencyKey: string,
  ): Promise<PaymentMethodSessionResponse> {
    const response = await this.authorizedFetch(`${this.baseURL}/me/billing/payment-method`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "Idempotency-Key": idempotencyKey,
      },
      body: JSON.stringify({
        success_url: successUrl,
        cancel_url: cancelUrl,
      }),
    });

    return this.parseJSON<PaymentMethodSessionResponse>(response, "Failed to create payment method session");
  }

  async getBillingPlan(): Promise<BillingPlanResponse> {
    const response = await this.authorizedFetch(`${this.baseURL}/me/billing/plan`, {
      method: "GET",
      headers: { Accept: "application/json" },
    });

    return this.parseJSON<BillingPlanResponse>(response, "Failed to fetch billing plan");
  }

  async changePlan(
    newPlanKey: string,
    componentConfigurations?: ComponentConfiguration[],
    idempotencyKey?: string,
  ): Promise<ChangePlanResponse> {
    const body: Record<string, unknown> = { new_plan_key: newPlanKey };
    if (componentConfigurations) body.component_configurations = componentConfigurations;

    const response = await this.authorizedFetch(`${this.baseURL}/me/billing/change-plan`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}),
      },
      body: JSON.stringify(body),
    });

    return this.parseJSON<ChangePlanResponse>(response, "Failed to change plan");
  }

  async cancelPlan(idempotencyKey: string): Promise<CancelPlanResponse> {
    const response = await this.authorizedFetch(`${this.baseURL}/me/billing/cancel-plan`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "Idempotency-Key": idempotencyKey,
      },
      body: "{}",
    });

    return this.parseJSON<CancelPlanResponse>(response, "Failed to cancel plan");
  }

  // One-Off Session Methods

  async previewFeed(
    params: { feed_url: string } | { podcast_slug: string }
  ): Promise<FeedPreview> {
    const body = "feed_url" in params
      ? { feed_url: params.feed_url.trim() }
      : { podcast_slug: params.podcast_slug };

    const response = await this.authorizedFetch(`${this.baseURL}/me/one-off/preview`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(body),
    });

    return this.parseJSON<FeedPreview>(response, "Failed to preview feed");
  }

  async createOneOffSession(
    params:
      | { feed_url: string; episode_indices: number[] }
      | { podcast_slug: string; episode_numbers: number[] }
  ): Promise<OneOffSession> {
    const body = "feed_url" in params
      ? { feed_url: params.feed_url.trim(), episode_indices: params.episode_indices }
      : { podcast_slug: params.podcast_slug, episode_numbers: params.episode_numbers };

    const response = await this.authorizedFetch(`${this.baseURL}/me/one-off/sessions`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(body),
    });

    return this.parseJSON<OneOffSession>(response, "Failed to create session");
  }

  async getOneOffSession(): Promise<OneOffSession> {
    const response = await this.authorizedFetch(`${this.baseURL}/me/one-off/sessions/current`, {
      method: "GET",
      headers: { Accept: "application/json" },
    });

    return this.parseJSON<OneOffSession>(response, "Failed to fetch session");
  }

  async askOneOffSession(payload: OneOffAskRequest): Promise<OneOffAskResponse> {
    const response = await this.authorizedFetch(`${this.baseURL}/me/one-off/sessions/current/ask`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    });

    return this.parseJSON<OneOffAskResponse>(response, "Failed to ask question");
  }

  async deleteOneOffSession(): Promise<void> {
    const response = await this.authorizedFetch(`${this.baseURL}/me/one-off/sessions/current`, {
      method: "DELETE",
    });

    if (!response.ok && response.status !== 204) {
      throw new Error(`Failed to delete session: ${response.status}`);
    }
  }

  // Ask Sessions Methods (persistent RAG chat sessions)

  async createAskSession(
    podcastSlug: string,
    idempotencyKey?: string,
  ): Promise<AskSession> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    if (idempotencyKey) {
      headers["Idempotency-Key"] = idempotencyKey;
    }

    const response = await this.authorizedFetch(
      `${this.baseURL}/podcasts/${encodeURIComponent(podcastSlug)}/sessions`,
      {
        method: "POST",
        headers,
        body: JSON.stringify({}),
      },
    );

    return this.parseJSON<AskSession>(response, "Failed to create session");
  }

  async listAskSessions(podcastSlug: string): Promise<ListAskSessionsResponse> {
    const response = await this.authorizedFetch(
      `${this.baseURL}/podcasts/${encodeURIComponent(podcastSlug)}/sessions`,
      {
        method: "GET",
        headers: { Accept: "application/json" },
      },
    );

    return this.parseJSON<ListAskSessionsResponse>(response, "Failed to list sessions");
  }

  async listAllSessions(): Promise<ListAskSessionsResponse> {
    const response = await this.authorizedFetch(
      `${this.baseURL}/me/sessions`,
      {
        method: "GET",
        headers: { Accept: "application/json" },
      },
    );

    return this.parseJSON<ListAskSessionsResponse>(response, "Failed to list sessions");
  }

  async getAskSession(
    podcastSlug: string,
    sessionId: string,
  ): Promise<AskSessionWithMessages> {
    const response = await this.authorizedFetch(
      `${this.baseURL}/podcasts/${encodeURIComponent(podcastSlug)}/sessions/${encodeURIComponent(sessionId)}`,
      {
        method: "GET",
        headers: { Accept: "application/json" },
      },
    );

    return this.parseJSON<AskSessionWithMessages>(response, "Failed to fetch session");
  }

  async deleteAskSession(podcastSlug: string, sessionId: string): Promise<void> {
    const response = await this.authorizedFetch(
      `${this.baseURL}/podcasts/${encodeURIComponent(podcastSlug)}/sessions/${encodeURIComponent(sessionId)}`,
      {
        method: "DELETE",
      },
    );

    if (!response.ok && response.status !== 204) {
      throw new Error(`Failed to delete session: ${response.status}`);
    }
  }

  async askInSession(
    podcastSlug: string,
    sessionId: string,
    payload: AskInSessionRequest,
  ): Promise<AskInSessionResponse> {
    const response = await this.authorizedFetch(
      `${this.baseURL}/podcasts/${encodeURIComponent(podcastSlug)}/sessions/${encodeURIComponent(sessionId)}/ask`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      },
    );

    // Handle 410 Gone (session expired) specially
    if (response.status === 410) {
      throw new SessionExpiredError(sessionId);
    }

    return this.parseJSON<AskInSessionResponse>(response, "Failed to ask in session");
  }
}

export const apiClient = new APIClient();

export default APIClient;

export interface CreditGrantSummary {
  balance_cents: number;
  balance_formatted?: string;
  currency?: string;
}

export interface TopupResponse {
  invoice_id: string;
  payment_status: string;
  amount_cents: number;
  credit_type: string;
}

export interface PaymentMethodSessionResponse {
  session_id: string;
  checkout_url: string;
}

export interface BillingPlanResponse {
  subscription_status: "active" | "pending" | "canceled" | "past_due";
  stripe_plan_lookup_key: string | null;
  pricing_plan_subscription_id: string | null;
  stripe_customer_id: string | null;
}

export interface ComponentConfiguration {
  lookup_key: string;
  quantity: number;
}

export interface ChangePlanResponse {
  ok: boolean;
}

export interface CancelPlanResponse {
  ok: boolean;
}

// One-Off Session Types
export interface FeedPreviewEpisode {
  index: number;
  title: string;
  description: string;
  duration_seconds: number | null;
  published_date: string | null;
  audio_url?: string;
  episode_number?: number;      // present for cached/subscription sources
  has_transcript?: boolean;     // true if transcript exists in system
}

export interface FeedPreview {
  feed_title: string;
  feed_description: string;
  episodes: FeedPreviewEpisode[];
  podcast_slug?: string;        // present if recognized/cached
  source: "feed" | "cached" | "subscription";
}

export interface OneOffSessionEpisode {
  index: number;
  title: string;
  status: "queued" | "in_progress" | "ready" | "failed";
  token_count: number | null;
  error?: string;
}

export interface OneOffSessionMessage {
  role: "user" | "assistant";
  content: string;
  response_id: string | null;
  referenced_episode_indexes: number[] | null;
  created_at: string;
}

export interface OneOffSession {
  session_id: string;
  status: "transcribing" | "ready" | "partial" | "failed" | "expired";
  feed_title: string;
  episodes: OneOffSessionEpisode[];
  total_tokens?: number;
  model_context_limit?: number;
  expires_at: string;
  messages?: OneOffSessionMessage[];
  message_count?: number;
}

export interface OneOffAskRequest {
  question: string;
  enable_thinking?: boolean;
  include_episodes?: number[];
  previous_response_id?: string;
}

export interface OneOffAskResponse {
  verdict: "answer" | "insufficient";
  answer: string;
  response_id: string;
  model: string;
  chunks_used: number;
  usage: {
    input_tokens: number;
    output_tokens: number;
    context_reset: boolean;
    cost_usd?: number;
  };
}

// Ask Sessions Types (persistent RAG chat sessions)
export interface AskSession {
  session_id: string;
  podcast_slug: string;
  podcast_display_name: string;
  status: "active" | "expired";
  first_question_snippet: string | null;
  created_at: string;
  expires_at: string;
  last_activity_at: string;
  message_count?: number;
}

export interface AskSessionMessage {
  id: number;
  role: "user" | "assistant";
  content: string;
  response_id: string | null;
  referenced_episode_numbers: number[] | null;
  metadata?: AskResponseMetadata | null;
  created_at: string;
}

export interface AskSessionWithMessages extends AskSession {
  messages: AskSessionMessage[];
}

export interface ListAskSessionsResponse {
  sessions: AskSession[];
}

export interface AskInSessionRequest {
  question: string;
  enable_thinking?: boolean;
  k?: number;
  max_context_tokens?: number;
  restrict_episode_number?: number;
  restrict_title_key?: string;
  mmr_diversity?: number;
  score_threshold?: number;
}

export interface AskInSessionResponse {
  verdict: "answer" | "no_context";
  used_context: "current" | "reset";
  answer: string;
  response_id: string;
  model: string;
  chunks_used: number;
  referenced_episode_numbers: number[];
  metadata?: AskResponseMetadata | null;
  usage: {
    prompt_tokens: number;
    completion_tokens: number;
    context_reset: boolean;
  };
}

// Custom error for expired sessions
export class SessionExpiredError extends Error {
  constructor(public sessionId: string) {
    super(`Session expired: ${sessionId}`);
    this.name = "SessionExpiredError";
  }
}
